import { inject, newInstanceForScope } from 'aurelia';
import { IRouter, Params } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import { CustomValidationControllerFactory } from '../../infrastructure/validation/custom-validation-controller-factory';
import { IValidationController } from '@aurelia/validation-html';
import { Project } from '../../model/project';
import { EventEntity } from '../../apis/event/event-entity';
import { UserEntity } from '../../apis/user/user-entity';
import { IEventPresenterScheduleData, IEventPresenterViewModel } from '../../components/info/event-presenter';
import { ScheduleEntity } from '../../apis/schedule/schedule-entity';
import { ScheduleRowEntity } from '../../apis/schedule/schedule-row-entity';
import { UserContext } from '../../contexts/user-context';
import { VenueEntity } from '../../apis/venue/venue-entity';
import { ProjectModelService } from '../../services/project-model-service';
import { SchedulerModelService } from '../../services/scheduler-model-service';
import { SchedulerModel } from '../../model/scheduler-model';
import { GroupEntity } from '../../apis/group/group-entity';
import { EventCommentEntity } from '../../apis/event/event-comment-entity';
import { ScheduleFieldEntity } from '../../apis/schedule/schedule-field-entity';
import { ProjectDraftStorageService } from '../../services/project-draft-storage-service';
import { ScheduleDisplayNameService } from '../../services/schedule-display-name-service';

@inject(IRouter, CustomValidationControllerFactory, newInstanceForScope(IValidationController), ProjectModelService, UserContext, SchedulerModelService, ProjectDraftStorageService, I18N)
export class EventSummary {

    public captcha: boolean = false;
    public validationController: IValidationController;
    public showDone: boolean = false;
    public wasNewEvent: boolean = false;
    public isBusy: boolean = false;
    public isRedirecting: boolean = false;
    public onSavedInfoClosed: () => void;
    public onClosePreviewCallback: () => void;
    public showPreview: boolean = false;
    public newCommentText: string = '';
    public editingCommentId: string | null = null;
    public editCommentText: string = '';
    private _router: IRouter;
    private _project: Project;
    private _userContext: UserContext;

    private _eventPresenterModels: IEventPresenterViewModel[] = [];

    private _projectModelService: ProjectModelService;
    private _schedulerModelService: SchedulerModelService;
    private _projectDraftStorageService: ProjectDraftStorageService;
    private _i18n: I18N;
    private _inlineGroupRows: InlineGroupRow[] = [];

    constructor(router: IRouter, validationControllerFactory: CustomValidationControllerFactory, validationController: IValidationController, projectModelService: ProjectModelService, userContext: UserContext, schedulerModelService: SchedulerModelService, projectDraftStorageService: ProjectDraftStorageService, i18n: I18N) {
        this._router = router;
        this.validationController = validationControllerFactory.configure(validationController);
        this._projectModelService = projectModelService;
        this._userContext = userContext;
        this._schedulerModelService = schedulerModelService;
        this._projectDraftStorageService = projectDraftStorageService;
        this._i18n = i18n;
        this.onSavedInfoClosed = (): void => {
            this._onSavedInfoClosed();
        };
        this.onClosePreviewCallback = (): void => {
            this._closePreview();
        };

    }

    public async loading(params: Params): Promise<void> {
        try {
            if (params.eventId != null) {
                this._project = await this._projectModelService.loadByEventId(params.eventId, false);
                this._inlineGroupRows = [];
                for (const group of this._project.groups) {
                    this._inlineGroupRows.push(new InlineGroupRow(group));
                }
                this._sortInlineGroupRows();
            }
        } catch (error: unknown) {
            // State lost (e.g. page refresh on unsaved event) — redirect to start
            this.isRedirecting = true;
            setTimeout((): void => {
                void this._router.load('create');
            }, 0);
        }
    }

    public attached(): void {
        if (this.isRedirecting) { return; }
        this._updateSchedulerPresenterModels();
    }

    public get event(): EventEntity {
        return this._project.event;
    }

    public get groupNamePlaceholder(): string {
        return this._i18n.tr('ui:group_name_placeholder');
    }

    public get venues(): VenueEntity[] {
        return this._project.venues;
    }
    public get hasSchedules(): boolean {
        return this._project.schedules.any();
    }

    public get schedules(): ScheduleEntity[] {
        return this._project.schedules;
    }
    public get user(): UserEntity {
        return this._project?.user ?? this._userContext.userEntity;
    }
    public get isExistingEvent(): boolean {
        return this._project?.event.userId != null;
    }
    public get isNew(): boolean {
        return this._project?.event.code == null;
    }
    public get hasUnsavedChanges(): boolean {
        return this._projectModelService.hasUnsavedChanges;
    }
    public get comments(): EventCommentEntity[] {
        return this._project?.comments ?? [];
    }
    public get isLoggedIn(): boolean {
        return this._userContext.isLoggedIn;
    }
    public get eventPresenterModels(): IEventPresenterViewModel[] {
        return this._eventPresenterModels;
    }

    public get directLinkUrl(): string {
        return `${window.location.origin}/${this.event.code}`;
    }

    public get directLinkDisplay(): string {
        return `${window.location.host}/${this.event.code}`;
    }

    public get showRegistrationLink(): boolean {
        return !this.isNew && this.event.registrationEnabled;
    }

    public get registrationLinkUrl(): string {
        return `${window.location.origin}/registration/${this.event.code}`;
    }

    public get registrationLinkDisplay(): string {
        return `${window.location.host}/registration/${this.event.code}`;
    }

    public get eventDateDisplay(): string {
        const start = this._project?.event?.startDate?.toString();
        const end = this._project?.event?.endDate?.toString();
        if (!start) { return '–'; }
        if (!end || end === start) { return start; }
        return `${start} – ${end}`;
    }

    public get groups(): GroupEntity[] {
        return this._project?.groups ?? [];
    }

    public get inlineGroupRows(): InlineGroupRow[] {
        return this._inlineGroupRows;
    }

    public get hasGroups(): boolean {
        return this._inlineGroupRows.length > 0;
    }

    public get hasNewGroupRow(): boolean {
        return this._inlineGroupRows.some(r => r.group.id == null);
    }

    public teamCountForGroup(groupId: string): number {
        return this._projectModelService.getTeamsForGroups([groupId]).length;
    }

    public scheduleDisplayName(schedule: ScheduleEntity): string {
        return ScheduleDisplayNameService.getDisplayName(schedule, this._project.groups);
    }

    public manageTeams(group: GroupEntity): void {
        void this._router.load(`create/edit-teams/${this._project.event.id}/${group.id}`);
    }

    public addGroup(): void {
        const newGroup = new GroupEntity();
        const row = new InlineGroupRow(newGroup, true);
        this._inlineGroupRows.push(row);
        setTimeout(() => {
            const inputs = document.querySelectorAll<HTMLInputElement>('.group-name-input');
            const last = inputs[inputs.length - 1];
            if (last) { last.focus(); }
        }, 0);
    }

    public async commitGroupName(row: InlineGroupRow, value?: string): Promise<void> {
        if (value != null) {
            row.editName = value;
        }
        const name = row.editName.trim();
        if (row.group.id == null) {
            if (!name) {
                const idx = this._inlineGroupRows.indexOf(row);
                if (idx >= 0) { this._inlineGroupRows.splice(idx, 1); }
                return;
            }
            const added = this._projectModelService.addGroup(name);
            row.group = added;
        } else {
            if (!name) {
                row.editName = row.group.name ?? '';
            } else if (name !== row.group.name) {
                this._projectModelService.updateGroup(row.group.id, name);
                row.group.name = name;
            }
        }
        row.isEditing = false;
    }

    public deleteGroup(row: InlineGroupRow): void {
        const group = row.group;
        if (group.id == null) {
            const pendingIdx = this._inlineGroupRows.indexOf(row);
            if (pendingIdx >= 0) { this._inlineGroupRows.splice(pendingIdx, 1); }
            return;
        }
        this._projectModelService.removeGroup(group.id);
        const idx = this._inlineGroupRows.indexOf(row);
        if (idx >= 0) { this._inlineGroupRows.splice(idx, 1); }
    }

    public canRemoveGroup(groupId: string, _?: number): boolean {
        return this._projectModelService.canRemoveGroup(groupId);
    }

    public addSchedule(): void {
        void this._router.load(`create/edit-serie/${this._project.event.id}`);
    }
    public editEvent(): void {
        void this._router.load(`create/edit-event/${this._project.event.id}`);
    }

    public editSchedule(schedule: ScheduleEntity): void {
        const pending = this._projectModelService.pendingSchedulerModels
            .find(m => m.scheduleId === schedule.id);
        if (pending != null) {
            this._schedulerModelService.setModelCache(pending);
        }
        void this._router.load(`create/edit-serie/${schedule.eventId}/${schedule.id}`);
    }

    public deleteSchedule(schedule: ScheduleEntity): void {
        this._projectModelService.removeSchedule(schedule.id);
    }

    public moveScheduleUp(schedule: ScheduleEntity): void {
        this._projectModelService.reorderSchedule(schedule.id, 'up');
    }

    public moveScheduleDown(schedule: ScheduleEntity): void {
        this._projectModelService.reorderSchedule(schedule.id, 'down');
    }

    public async goToLogin(): Promise<void> {
        const eventId = this._project?.event?.id ?? '';
        this._projectDraftStorageService.saveDraft();
        await this._router.load(`login?returnUrl=create/event-summary/${eventId}`);
    }

    public async save(): Promise<void> {
        if (!this._userContext.isLoggedIn) {
            await this.goToLogin();
            return;
        }
        this.isBusy = true;
        try {
            this.wasNewEvent = this.isNew;
            await this._projectModelService.saveProject();
            this._projectDraftStorageService.clearDraft();
            this.showDone = true;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Save failed:', err);
        } finally {
            this.isBusy = false;
        }
    }

    public resolveGroupName(groupId: string): string {
        const group = this._project.groups.firstOrDefault(g => g.id === groupId);
        return group != null ? group.name : 'Ingen klass vald';

    }
    public cancel(): void {
        // Discard the in-memory draft first — otherwise Create.loading() resumes this
        // same unsaved event and bounces straight back to event-summary.
        this._projectModelService.clear();
        void this._router.load('create');
    }

    public openPreview(): void {
        this._updateSchedulerPresenterModels();
        this.showPreview = true;
    }

    public manageVenues(): void {
        void this._router.load(`create/manage-venues/${this._project.event.id}`);
    }

    private _closePreview(): void {
        this.showPreview = false;
    }

    private _sortInlineGroupRows(): void {
        this._inlineGroupRows.sort((a, b) => (a.group.name ?? '').localeCompare(b.group.name ?? ''));
    }

    private _updateSchedulerPresenterModels(): void {
        this._eventPresenterModels.clear();
        for (const schedule of this._project.schedules) {
            const pending = this._projectModelService.pendingSchedulerModels
                .find(m => m.scheduleId === schedule.id);
            if (pending != null) {
                this._eventPresenterModels.add(new PendingEventPresenterViewModel(pending, this._project.venues, this._projectModelService.getFieldsForSchedule(pending.scheduleId)));
            } else {
                this._eventPresenterModels.add(new ProjectModelEventPresentViewModel(schedule, this._projectModelService, this._project.venues));
            }
        }
    }

    private _onSavedInfoClosed(): void {
        this.showDone = false;
    }

    public addComment(): void {
        const text = this.newCommentText?.trim();
        if (!text || this.isNew) { return; }
        this._projectModelService.addComment(text);
        this.newCommentText = '';
    }

    public deleteComment(comment: EventCommentEntity): void {
        this._projectModelService.deleteComment(comment);
    }

    public startEditComment(comment: EventCommentEntity): void {
        this.editingCommentId = comment.id;
        this.editCommentText = comment.text;
    }

    public cancelEditComment(): void {
        this.editingCommentId = null;
        this.editCommentText = '';
    }

    public saveEditComment(comment: EventCommentEntity): void {
        const text = this.editCommentText?.trim();
        if (!text) { return; }
        this._projectModelService.updateComment(comment, text);
        this.editingCommentId = null;
        this.editCommentText = '';
    }
}

export class PendingEventPresenterViewModel implements IEventPresenterViewModel {
    private _model: SchedulerModel;
    private _venues: VenueEntity[];
    private _fields: ScheduleFieldEntity[];
    constructor(model: SchedulerModel, venues: VenueEntity[], fields: ScheduleFieldEntity[]) {
        this._model = model;
        this._venues = venues;
        this._fields = fields;
    }
    public get schedule(): ScheduleEntity { return this._model.toEntity(); }
    public get code(): string { return null; }
    public get name(): string { return this._model.name; }
    public get games(): ScheduleRowEntity[] { return this._model.games; }
    public getPresentationData(): Promise<IEventPresenterScheduleData> {
        return Promise.resolve({
            serie: this._model.serie,
            customIntervals: this._model.customIntervals,
            venues: this._venues,
            teams: this._model.teamSettings ?? [],
            fields: this._fields
        });
    }
}

export class ProjectModelEventPresentViewModel implements IEventPresenterViewModel {

    private _entity: ScheduleEntity;
    private _projectModelService: ProjectModelService;
    private _venues: VenueEntity[];

    constructor(entity: ScheduleEntity, projectModelService: ProjectModelService, venues: VenueEntity[]) {
        this._entity = entity;
        this._projectModelService = projectModelService;
        this._venues = venues;
    }
    public get schedule(): ScheduleEntity {
        return this._entity;
    }
    public get code(): string {
        return this._entity.code;
    }
    public get name(): string {
        return this._entity.name;
    }
    public get games(): ScheduleRowEntity[] {
        return this.schedule.games;
    }

    public getPresentationData(): Promise<IEventPresenterScheduleData> {
        return Promise.resolve({
            serie: this._projectModelService.getSerieForSchedule(this._entity.id),
            customIntervals: this._projectModelService.getCustomIntervalsForSchedule(this._entity.id),
            venues: this._venues,
            teams: this._projectModelService.getTeamsForGroups(this._entity.groupIds ?? []),
            fields: this._projectModelService.getFieldsForSchedule(this._entity.id)
        });
    }
}

export class InlineGroupRow {
    public group: GroupEntity;
    public isEditing: boolean;
    public editName: string;

    constructor(group: GroupEntity, isNew: boolean = false) {
        this.group = group;
        this.editName = group.name ?? '';
        this.isEditing = isNew;
    }
}
